// A reusable page header component with modern, professional styling
import { Button } from "@/components/ui/button"
import { LucideIcon } from "lucide-react"
import { ReactNode } from "react"

interface PageHeaderProps {
    title: string
    description: string | ReactNode
    icon?: ReactNode
    badge?: {
        label: string
        variant: string
    }
    actions?: ReactNode
    action?: {
        label: string
        icon: LucideIcon
        onClick: () => void
    }
}

export function PageHeader({ title, description, icon, badge, actions, action }: PageHeaderProps) {
    return (
        <div className="pb-4 mb-0 w-full">
            <div className="flex justify-between items-start gap-4">
                <div className="flex items-start gap-3">
                    {icon && <div className="flex-shrink-0 text-primary mt-1">{icon}</div>}
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
                            {badge && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                                    badge.variant === 'success' ? 'bg-green-100 text-green-800' : 
                                    badge.variant === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
                                    'bg-blue-100 text-blue-800'
                                }`}>
                                    {badge.label}
                                </span>
                            )}
                        </div>
                        <div className="text-muted-foreground text-sm mt-1">
                            {description}
                        </div>
                    </div>
                </div>
                {(action || actions) && (
                    <div className="flex items-center gap-2">
                        {action && (
                            <Button onClick={action.onClick} size="sm" className="h-9">
                                <action.icon className="h-4 w-4 mr-2" />
                                {action.label}
                            </Button>
                        )}
                        {actions}
                    </div>
                )}
            </div>
        </div>
    )
}

export const PageHeaderWithElement = PageHeader; 